/**
 * feat-338 — the expanded question (design 02, NOTES §4).
 *
 * Fetches `GET /questions/{id}` with the page's current filters. Order: full question
 * + actions → why it's flagged → how the answers ended → by store / by language →
 * FITB only: rejected + accepted-after-AI → "No names" footer. A multiple-choice
 * question has NO wrong-answer section at all (that data does not exist). A
 * suppressed question shows the question only: no stats and no Suggest edits.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ExternalLink, Loader2 } from 'lucide-react';
import {
  BreakdownMetrics,
  Cell,
  getQuestionDetail,
  isSuppressed,
  LangBreakdown,
  LocationBreakdown,
  QuestionAnalysisFilters,
  QuestionDetailResponse,
  QuestionsMeta,
  ResultMix,
} from '../../../../services/questionAnalysisService';
import {
  C,
  FLAG,
  QA_COPY,
  QA_DETAIL_COPY as D,
  TYPE_LABEL,
  formatAnswerSeconds,
  langLabel,
  pct,
  pctTitle,
} from './questionAnalysisFormat';
import { ErrorBanner, FewerThan, SuppressedValue, httpStatusOf, orDash, useSuppressFloor } from './parts';
import { SuggestButton, SuggestPanel, useSuggestEdits } from './SuggestEdits';

/** Where "Open in Learn Module" goes (DESIGN-QUESTION Q1: the editor has no deep link). */
export const LEARN_MODULE_PATH = '/quiz-management';

interface Props {
  tenantId: string;
  questionId: string;
  filters: QuestionAnalysisFilters;
  meta: QuestionsMeta;
}

const MIX_SEGMENTS: { key: keyof ResultMix; color: string }[] = [
  { key: 'pass_first', color: 'bg-success' },
  { key: 'pass_mc_partial', color: 'bg-warning-solid' },
  { key: 'pass_retry', color: 'bg-ink/40' },
  { key: 'fail', color: 'bg-danger' },
];

type BreakdownCell = Cell<object, BreakdownMetrics>;

function Breakdown({
  title,
  nameHead,
  rows,
  testId,
}: {
  title: string;
  nameHead: string;
  rows: { name: string; cell: BreakdownCell }[];
  testId: string;
}) {
  const cell = 'px-3 py-2 border-t border-softBorder';
  return (
    <div className="min-w-0" data-testid={testId}>
      <h4 className={C.h4}>{title}</h4>
      {rows.length === 0 ? (
        <p className="text-sm text-inkMuted">{D.noBreakdown}</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-softBorder">
          <table className="w-full min-w-[300px] text-sm border-separate border-spacing-0 bg-white">
            <thead>
              <tr className="bg-cream-dark text-[11px] font-bold uppercase tracking-wide text-inkMuted">
                <th scope="col" className="px-3 py-2 text-left">
                  {nameHead}
                </th>
                <th scope="col" className="px-3 py-2 text-right">
                  {D.colLearners}
                </th>
                <th scope="col" className="px-3 py-2 text-right">
                  {D.colWrong}
                </th>
                <th scope="col" className="px-3 py-2 text-right">
                  {D.colTime}
                </th>
              </tr>
            </thead>
            <tbody className="text-ink">
              {rows.map(({ name, cell: r }) => (
                <tr key={name} data-testid="breakdown-row">
                  <th scope="row" className={`${cell} text-left font-semibold`}>
                    {name}
                  </th>
                  {isSuppressed(r) ? (
                    <td colSpan={3} className={`${cell} text-right`}>
                      <SuppressedValue inline />
                    </td>
                  ) : (
                    <>
                      <td className={`${cell} text-right`}>{r.learners}</td>
                      <td className={`${cell} text-right font-bold`} title={pctTitle(r.first_try_wrong_rate)}>
                        {orDash(pct(r.first_try_wrong_rate))}
                      </td>
                      <td className={`${cell} text-right`}>{orDash(formatAnswerSeconds(r.median_answer_ms))}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const QuestionDetailPanel: React.FC<Props> = ({ tenantId, questionId, filters, meta }) => {
  const suggest = useSuggestEdits(tenantId, questionId, filters);
  const floor = useSuppressFloor();
  const [detail, setDetail] = useState<QuestionDetailResponse | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const seq = useRef(0);
  const filtersKey = JSON.stringify(filters);

  const load = useCallback(() => {
    const my = ++seq.current;
    setLoading(true);
    setError(null);
    getQuestionDetail(tenantId, questionId, JSON.parse(filtersKey)).then(
      (d) => {
        if (my !== seq.current) return;
        setDetail(d);
        setLoading(false);
      },
      (e) => {
        if (my !== seq.current) return;
        setError(e ?? new Error('failed'));
        setLoading(false);
      },
    );
  }, [tenantId, questionId, filtersKey]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !detail) {
    return (
      <div className="flex items-center gap-2 text-sm text-inkMuted" role="status" data-testid="detail-loading">
        <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
        {D.loading}
      </div>
    );
  }
  if (error || !detail) {
    return (
      <ErrorBanner
        testId="detail-error"
        title={D.failed}
        detail={QA_COPY.failedDetail(httpStatusOf(error))}
        onRetry={load}
        busy={loading}
      />
    );
  }

  const q = detail.question;
  const stats = detail.stats;
  const isFitb = q.question_type === 'fill_blank';
  const canSuggest = !stats.suppressed;

  return (
    <div className="space-y-5" aria-label={D.label} data-testid="detail-content">
      <div className="flex flex-wrap items-start gap-4">
        <div className="min-w-0 flex-1 basis-[420px]">
          <h3 className="text-xs font-bold uppercase tracking-wide text-inkMuted mb-1">
            {D.fullQuestion(TYPE_LABEL[q.question_type])}
          </h3>
          <p className="font-display text-xl font-semibold text-ink leading-snug break-words" data-testid="full-question">
            {q.question_text}
          </p>
          <dl className="mt-2 text-sm space-y-1">
            {isFitb && q.accepted_answers && q.accepted_answers.length > 0 && (
              <div className="flex flex-wrap gap-x-2">
                <dt className="text-inkMuted">{D.acceptedAnswers}</dt>
                <dd className="flex flex-wrap gap-1.5" data-testid="accepted-answers">
                  {q.accepted_answers.map((a) => (
                    <span key={a} className="rounded-full bg-success-light text-success-on text-xs font-bold px-2 py-0.5">
                      {a}
                    </span>
                  ))}
                </dd>
              </div>
            )}
            {!isFitb && q.options && q.options.length > 0 && (
              <div className="flex flex-wrap gap-x-2">
                <dt className="text-inkMuted">{D.options}</dt>
                <dd className="min-w-0" data-testid="options">
                  <ol className="space-y-1">
                    {q.options.map((o, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="text-inkMuted w-4 shrink-0">{String.fromCharCode(65 + i)}</span>
                        <span className="min-w-0 text-ink break-words">
                          {o}
                          {i === q.correct_index && (
                            <span className="ml-1 rounded-full bg-success-light text-success-on text-[11px] font-bold px-1.5 py-0.5">
                              {D.correct}
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ol>
                </dd>
              </div>
            )}
            {(q.explanation || q.correct_explanation) && (
              <div className="flex flex-wrap gap-x-2">
                <dt className="text-inkMuted">{D.explanation}</dt>
                <dd className="text-ink break-words">{q.explanation || q.correct_explanation}</dd>
              </div>
            )}
            <div className="flex flex-wrap gap-x-2">
              <dt className="text-inkMuted">{D.lesson}</dt>
              <dd className="text-ink">
                {q.drill_name} <span className="text-inkMuted">· {q.lesson_name}</span>
              </dd>
            </div>
            {/* feat-354 — `copies` is on detail.stats (CONTRACT C3), not detail.question. */}
            {(stats.copies ?? 0) > 0 && (
              <div className="flex flex-wrap gap-x-2" data-testid="detail-copies">
                <dt className="text-inkMuted">{D.copies}</dt>
                <dd className="text-ink">
                  {QA_COPY.copies(stats.copies!)} <span className="text-inkMuted">{D.copiesTail}</span>
                </dd>
              </div>
            )}
          </dl>
        </div>
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <a
            href={LEARN_MODULE_PATH}
            target="_blank"
            rel="noopener noreferrer"
            className={C.btnSecondary}
            title={D.openInLearnTitle(q.drill_name)}
            data-testid="open-in-learn"
          >
            {D.openInLearn}
            <ExternalLink className="w-4 h-4" aria-hidden="true" />
          </a>
          {canSuggest && <SuggestButton state={suggest.state} onClick={suggest.run} />}
        </div>
      </div>

      {canSuggest && (
        <SuggestPanel state={suggest.state} question={q} onRetry={suggest.run} timeZone={meta.range.timezone} />
      )}

      {stats.suppressed ? (
        <p className="text-sm text-inkMuted" data-testid="detail-suppressed">
          {D.suppressedNote(floor)}
        </p>
      ) : (
        <>
          {stats.flags.length > 0 && (
            <div data-testid="why-flagged">
              <h4 className={C.h4}>{D.whyFlagged}</h4>
              <ul className="space-y-1.5">
                {stats.flags.map((f) => {
                  const def = FLAG[f];
                  const usual = meta.type_median_answer_ms[stats.question_type] ?? null;
                  const actual =
                    f === 'often_missed'
                      ? D.actual.often_missed(pct(stats.first_try_wrong_rate) ?? '—')
                      : f === 'slow'
                        ? D.actual.slow(formatAnswerSeconds(stats.median_answer_ms) ?? '—', formatAnswerSeconds(usual) ?? '—')
                        : f === 'quit'
                          ? D.actual.quit(pct(stats.exit_rate) ?? '—')
                          : D.actual.near_miss(stats.fitb?.rejected_learners ?? null, floor);
                  return (
                    <li key={f} className="flex flex-wrap items-center gap-2 text-sm">
                      <span className={C.flagChip}>
                        <def.Icon className="w-3 h-3" aria-hidden="true" />
                        {def.label}
                      </span>
                      <span className="text-ink font-semibold">{actual}</span>
                      <span className="text-inkMuted">
                        · {D.rule} {def.rule(meta.thresholds)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div data-testid="result-mix">
            <h4 className={C.h4}>{D.howEnded(stats.attempts)}</h4>
            <div className="flex h-3 w-full max-w-[520px] rounded-full overflow-hidden bg-cream-dark" aria-hidden="true">
              {MIX_SEGMENTS.map(({ key, color }) => (
                <div
                  key={key}
                  className={color}
                  style={{ width: `${stats.attempts ? (stats.result_mix[key] / stats.attempts) * 100 : 0}%` }}
                />
              ))}
            </div>
            <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm max-w-[640px]">
              {MIX_SEGMENTS.map(({ key, color }) => (
                <li key={key} className="flex items-center gap-2" data-testid={`mix-${key}`}>
                  <span className={`inline-block w-2.5 h-2.5 rounded-full ${color}`} aria-hidden="true" />
                  <span className="text-ink">{D.mix[key]}</span>
                  <span className="ml-auto font-bold text-ink">
                    {stats.attempts ? pct(stats.result_mix[key] / stats.attempts) : '—'}
                  </span>
                  <span className="text-xs text-inkMuted w-10 text-right">{stats.result_mix[key]}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-inkMuted mt-1.5" data-testid="still-wrong-line">
              {stats.still_wrong_after_retry_rate == null ? (
                D.stillWrongNone
              ) : (
                <>
                  {D.stillWrongLead}
                  <b className="text-ink">{pct(stats.still_wrong_after_retry_rate)}</b>
                  {D.stillWrongTail}
                </>
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Breakdown
              title={D.byStore}
              nameHead={D.colStore}
              rows={detail.by_location.map((r: LocationBreakdown) => ({ name: r.location_name, cell: r }))}
              testId="by-store"
            />
            <Breakdown
              title={D.byLanguage}
              nameHead={D.colLanguage}
              rows={detail.by_lang.map((r: LangBreakdown) => ({ name: langLabel(r.lang), cell: r }))}
              testId="by-lang"
            />
          </div>

          {isFitb && detail.fitb_answers && (
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-5" data-testid="fitb-answers">
              <div className="min-w-0">
                <h4 className="text-xs font-bold uppercase tracking-wide text-inkMuted mb-0.5">{D.rejectedTitle}</h4>
                <p className="text-xs text-inkMuted mb-1.5">{D.rejectedSub}</p>
                {detail.fitb_answers.rejected.length ? (
                  <ol className="rounded-xl border border-softBorder bg-white divide-y divide-[rgba(0,0,0,0.08)]" data-testid="rejected-list">
                    {detail.fitb_answers.rejected.slice(0, 10).map((r) => (
                      <li key={r.answer} className="px-3 py-2">
                        <div className="flex items-baseline gap-3">
                          <span className="font-bold text-ink break-words min-w-0">“{r.answer}”</span>
                          <span className="ml-auto shrink-0 text-xs font-bold text-inkMuted whitespace-nowrap">
                            {r.learners == null ? <FewerThan floor={floor} suffix=" learners" /> : D.learnersN(r.learners)}
                          </span>
                        </div>
                        {r.sample_reason && (
                          <p className="text-xs text-inkMuted mt-0.5 break-words">
                            {D.reasonLead}
                            {r.sample_reason}
                          </p>
                        )}
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="text-sm text-inkMuted">{D.rejectedNone}</p>
                )}
              </div>
              <div className="min-w-0">
                <h4 className="text-xs font-bold uppercase tracking-wide text-inkMuted mb-0.5">{D.acceptedTitle}</h4>
                <p className="text-xs text-inkMuted mb-1.5">{D.acceptedSub}</p>
                {detail.fitb_answers.accepted_after_ai.length ? (
                  <ol className="rounded-xl border border-softBorder bg-white divide-y divide-[rgba(0,0,0,0.08)]" data-testid="accepted-list">
                    {detail.fitb_answers.accepted_after_ai.slice(0, 5).map((r) => (
                      <li key={r.answer} className="px-3 py-2 flex items-baseline gap-3">
                        <span className="font-bold text-ink break-words min-w-0">“{r.answer}”</span>
                        <span className="ml-auto shrink-0 text-xs font-bold text-inkMuted whitespace-nowrap">
                          {r.learners == null ? <FewerThan floor={floor} suffix=" learners" /> : D.learnersN(r.learners)}
                        </span>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="text-sm text-inkMuted">{D.acceptedNone}</p>
                )}
              </div>
            </div>
          )}
        </>
      )}
      <p className="text-xs text-inkMuted">{D.footer(floor)}</p>
    </div>
  );
};

export default QuestionDetailPanel;
